import AuthLayout from '../../components/layout/AuthLayout.jsx';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import BaseButton from '../../components/form/BaseButton.jsx';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useCurrentUser from '../../hooks/useCurrentUser.js';
import { useGetFileCategoriesQuery, useLazyGetFilesQuery, useMassDeleteFilesMutation } from '../../api/file.js';
import { PERMISSION_FILES } from '../../utils/constants.js';
import { downloadFile } from '../../utils/file.js';
import FilePreview from '../../components/misc/FilePreview.jsx';
import { useDebounce } from '../../hooks/useDebounce.js';
import BaseInput from '../../components/form/BaseInput.jsx';
import { MultiSelect } from 'primereact/multiselect';
import ThreeDotsLoader from '../../components/misc/ThreeDotsLoader.jsx';
import { useErrorToast } from '../../hooks/useToast.jsx';
import IconMultiSelect from '../../components/images/IconMultiSelect.jsx';
import { twMerge } from 'tailwind-merge';
import IconDelete from '../../components/images/IconDelete.jsx';
import IconDownload from '../../components/images/IconDownload.jsx';
import IconEditCompany from '../../components/images/IconEditCompany.jsx';
import IconMP3 from '../../components/images/IconMP3.jsx';
import IconPlay from '../../components/images/IconPlay.jsx';
import IconDocument from '../../components/images/IconDocument.jsx';
import IconSpreadsheet from '../../components/images/IconSpreadsheet.jsx';
import { canUpdateOrDelete } from '../../utils/user.js';

const FilesDashboard = () => {
    const organizationSwitch = useSelector((state) => state.organizationSwitch);
    const { user, permissions: userPermissions } = useCurrentUser();
    const navigate = useNavigate();
    const [filesByCategory, setFilesByCategory] = useState({});
    const [fileCounts, setFileCounts] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [visible, setVisible] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [search, setSearch] = useState('');
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [downloading, setDownloading] = useState(false);
    const debouncedSearch = useDebounce(search, 500);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [refetch, setRefetch] = useState(false);
    const [selectMode, setSelectMode] = useState(false);
    const [confirmMassDelete, setConfirmMassDelete] = useState(false);

    const { data: categoriesData, isLoading: categoriesLoading } = useGetFileCategoriesQuery();
    const [getFiles] = useLazyGetFilesQuery();

    const [massDeleteFiles, { isLoading: isLoadingMassDelete }] = useMassDeleteFilesMutation();

    const categories = useMemo(() => categoriesData?.response?.file_categories || [], [categoriesData]);

    const handleDownload = (file) => {
        downloadFile(file.url, file.title, file.extension, setDownloading).then();
    };

    const resetSearch = () => {
        setSearch('');
        setSelectedCategories([]);
    };

    const searchByValue = (value) => {
        setSearch(value);
    };

    const requestParams = useMemo(() => {
        let privateOrg = organizationSwitch[user?.id]?.private || null;
        let organizations = organizationSwitch[user?.id]?.organizations
            ? Object.keys(organizationSwitch[user?.id].organizations).filter(
                  (key) => organizationSwitch[user?.id].organizations[key]
              )
            : [];
        return {
            search: debouncedSearch,
            ...(organizations.length > 0 ? { organizations } : {}),
            ...(privateOrg !== null ? { private: privateOrg } : {})
        };
    }, [debouncedSearch, organizationSwitch, user?.id]);

    const fetchFiles = useCallback(async () => {
        setIsLoading(true);
        const fetchedFiles = {};
        const newFileCounts = {};

        const categoriesToFetch =
            selectedCategories.length > 0 ? selectedCategories : categories.map((cat) => cat.value);

        for (const category of categoriesToFetch) {
            try {
                const result = await getFiles({ category, ...requestParams, page: 1, per_page: 3 }).unwrap();

                fetchedFiles[category] = result?.response?.files || [];
                newFileCounts[category] = {
                    count: fetchedFiles[category].length,
                    has_more_pages: result.response.has_more_pages
                };
            } catch (error) {
                console.error(`Error fetching files for category ${category}:`, error);
            }
        }

        setFilesByCategory(fetchedFiles);
        setFileCounts(newFileCounts);
        setIsLoading(false);
    }, [getFiles, requestParams, selectedCategories, categories]);

    useEffect(() => {
        if (categories.length > 0) {
            fetchFiles();
        }
    }, [categories, fetchFiles]);

    const toggleSelectMode = () => {
        setSelectMode(!selectMode);
        setSelectedFiles([]);
    };

    const toggleFileSelection = (fileId) => {
        setSelectedFiles((prevSelected) =>
            prevSelected.includes(fileId) ? prevSelected.filter((id) => id !== fileId) : [...prevSelected, fileId]
        );
    };

    const deleteSelectedFiles = async () => {
        if (!selectedFiles.length) return;

        try {
            const response = await massDeleteFiles({ ids: selectedFiles }).unwrap();
            if (response.success) {
                setRefetch(true);
                fetchFiles();
                setSelectedFiles([]);
                setConfirmMassDelete(false);
                setSelectMode(false);
            } else {
                useErrorToast(response.message || 'Failed to delete files');
            }
        } catch (error) {
            console.error('Failed to delete files:', error);
        }
    };

    const loadMoreFiles = async (categoryId) => {
        const currentFiles = filesByCategory[categoryId] || [];
        const currentPage = Math.ceil(currentFiles.length / 3) + 1;

        try {
            const result = await getFiles({
                category: categoryId,
                ...requestParams,
                page: currentPage,
                per_page: 3
            }).unwrap();

            const newFiles = result?.response?.files || [];

            const mergedFiles = [
                ...currentFiles,
                ...newFiles.filter((newFile) => !currentFiles.some((existingFile) => existingFile.id === newFile.id))
            ];

            setFilesByCategory((prev) => ({
                ...prev,
                [categoryId]: mergedFiles
            }));

            setFileCounts((prev) => ({
                ...prev,
                [categoryId]: {
                    count: mergedFiles.length,
                    has_more_pages: result.response.has_more_pages
                }
            }));
        } catch (error) {
            console.error(`Error loading more files for category ${categoryId}:`, error);
        }
    };

    const fileDisplay = (file) => {
        const { category, url } = file;

        if (category === 'media') {
            if (url.match(/\.(jpg|jpeg|png|gif|webp|svg|tif|tiff|bmp|heic|heif)$/)) {
                return (
                    <div className='h-[107px] bg-theme1-white rounded-t-2xl shadow-md'>
                        <img src={url} alt='Media File' className='h-full w-full object-cover rounded-t-2xl' />
                    </div>
                );
            } else if (url.match(/\.(mp3|wav)$/)) {
                return (
                    <div className='h-[107px] bg-theme1-tan rounded-t-2xl flex items-center justify-center'>
                        <IconMP3 className='w-11 h-11 fill-theme1-primary' />
                    </div>
                );
            } else if (url.match(/\.(mp4|avi)$/)) {
                return (
                    <div className='h-[107px] bg-theme1-tan rounded-t-2xl flex items-center justify-center'>
                        <IconPlay className='w-11 h-11 stroke-theme1-primary' />
                    </div>
                );
            }
        } else if (category === 'document') {
            return (
                <div className='h-[107px] bg-theme1-tan bg-opacity-[85%] rounded-t-2xl flex items-center justify-center'>
                    <IconDocument className='w-11 h-11 stroke-theme1-primary' />
                </div>
            );
        } else if (category === 'spreadsheet') {
            return (
                <div className='h-[107px] bg-theme1-tan rounded-t-2xl flex items-center justify-center'>
                    <IconSpreadsheet className='w-11 h-11 stroke-theme1-primary' />
                </div>
            );
        }
    };

    const handlePreview = (file) => {
        setSelectedFile(file);
        setVisible(true);
    };

    const renderFiles = (categoryId) => {
        const category = categories.find((cat) => cat.value === categoryId);
        const categoryTitle = category?.label || 'Unknown category';
        const files = filesByCategory[categoryId] || [];
        const hasMoreFiles = fileCounts[categoryId]?.has_more_pages;

        return (
            <div key={categoryId} className='mb-8'>
                <h2 className='text-theme1-primary2 font-bold text-2xl mb-4'>{categoryTitle}</h2>
                {isLoading ? (
                    <ThreeDotsLoader />
                ) : (
                    <div className={`${files.length > 0 ? 'grid grid-cols-3 sm:grid-cols-4 gap-4' : ''}`}>
                        {files.length > 0 ? (
                            files.map((file) => (
                                <div
                                    key={file.id}
                                    className='border border-theme1-cardStroke bg-theme1-white rounded-2xl text-theme1-text1 flex flex-col relative'
                                >
                                    {selectMode && (
                                        <input
                                            id={'file-' + file.id}
                                            type='checkbox'
                                            className={twMerge(
                                                'h-8 w-8 appearance-none cursor-pointer rounded-full border border-theme1-primary left-4 top-4 bg-white',
                                                'checked:bg-white absolute',
                                                'before:absolute before:top-1/2 before:left-1/2 before:-translate-x-1/2 before:-translate-y-1/2',
                                                'before:content-[""] before:w-4 before:h-4 before:bg-no-repeat before:bg-center before:opacity-0',
                                                "checked:before:opacity-100 checked:before:bg-[url('/src/resources/images/checkmark.svg')]"
                                            )}
                                            value={file.id}
                                            checked={selectedFiles.includes(file.id)}
                                            onChange={() => toggleFileSelection(file.id)}
                                        />
                                    )}
                                    <div onClick={() => handlePreview(file)}>{fileDisplay(file)}</div>
                                    <div className='p-4'>
                                        <h3 className='text-sm font-bold truncate'>{file.title}</h3>
                                        <p className='text-sm text-theme1-text2 truncate'>{file.description}</p>
                                    </div>
                                    <div className='flex justify-between p-4'>
                                        <button
                                            type='button'
                                            disabled={downloading}
                                            onClick={() => handleDownload(file)}
                                        >
                                            <IconDownload className='w-8 h-8 fill-theme1-text3' />
                                        </button>
                                        {!!userPermissions[PERMISSION_FILES].update &&
                                            canUpdateOrDelete(user, file, PERMISSION_FILES, 'update') && (
                                                <button onClick={() => navigate(`/files/edit/${file.id}`)}>
                                                    <IconEditCompany className='w-9 h-9 fill-theme1-text3' />
                                                </button>
                                            )}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <p className='text-sm text-theme1-text2'>{`No ${search ? 'search results' : 'files'} available in this category.`}</p>
                        )}
                    </div>
                )}
                {files.length > 0 && hasMoreFiles && !isLoading && (
                    <div className='mt-4 text-center'>
                        <button
                            onClick={() => loadMoreFiles(categoryId)}
                            className='bg-none border-none text-theme1-title2 font-bold p-0 text-lg'
                            disabled={isLoading}
                        >
                            Load More
                        </button>
                    </div>
                )}
            </div>
        );
    };

    return (
        <AuthLayout title='Files Repository' backRoute='/dashboard'>
            <div className='px-4'>
                <div className='max-w-3xl w-full mx-auto'>
                    <div className='mt-6 mb-4 flex items-center'>
                        <BaseInput
                            name='search'
                            type='text'
                            value={search}
                            placeholder='Search...'
                            setValue={searchByValue}
                        />
                        <button onClick={resetSearch} className='text-theme1-primary2 ml-4'>
                            Reset
                        </button>
                    </div>

                    <div className='mb-4'>
                        <MultiSelect
                            value={selectedCategories}
                            options={categories}
                            onChange={(e) => setSelectedCategories(e.value)}
                            placeholder='Select categories'
                            className='w-full text-theme1-text1 rounded-2xl border border-theme1-text3 outline-none px-2 files-multi-select'
                            disabled={categoriesLoading}
                        />
                    </div>
                    <div className='mb-4 -mt-2 text-right'>
                        <Link to='/files/advanced-search' className='text-base text-theme1-title2 font-bold'>
                            Advanced Search
                        </Link>
                    </div>
                    {!!userPermissions[PERMISSION_FILES]?.delete && (
                        <div className='flex justify-between mt-4 mb-4'>
                            <div onClick={() => toggleSelectMode()} className='cursor-pointer'>
                                <IconMultiSelect className='w-6 h-6 fill-theme1-dark' />
                            </div>
                        </div>
                    )}
                </div>
                <div className='max-w-3xl w-full mx-auto'>
                    {isLoading ? (
                        <ThreeDotsLoader />
                    ) : (
                        <>{Object.keys(filesByCategory).map((categoryId) => renderFiles(categoryId))}</>
                    )}

                    {!!userPermissions[PERMISSION_FILES].read && !!userPermissions[PERMISSION_FILES].create && (
                        <div className={'px-5 mt-5 mb-10'}>
                            <BaseButton
                                id='btn-submit'
                                className='btn-primary-outline'
                                onClick={() => navigate('/files/import')}
                                textValue='Import'
                                disabled={isLoading}
                            />
                        </div>
                    )}
                </div>
                {selectedFile && (
                    <FilePreview
                        file={selectedFile}
                        visible={visible}
                        setVisible={setVisible}
                        setSelectedFile={setSelectedFile}
                    />
                )}
            </div>
            {selectMode && (
                <div className='z-20 fixed left-0 bottom-0 w-full bg-auth bg-center bg-cover rounded-t-4xl p-6'>
                    {confirmMassDelete ? (
                        <div>
                            <div className='text-white font-semibold text-lg text-center'>
                                Are you sure you want to delete {selectedFiles.length} selected files?
                            </div>
                            <div className='flex justify-between items-center gap-4 mt-6'>
                                <BaseButton
                                    className='btn-red-outline !text-theme1-white !border-theme1-white'
                                    textValue={'Cancel'}
                                    onClick={() => setConfirmMassDelete(false)}
                                />
                                <BaseButton
                                    className='btn-red-outline !text-theme1-white !border-theme1-white'
                                    textValue={'Delete'}
                                    onClick={() => deleteSelectedFiles()}
                                />
                            </div>
                        </div>
                    ) : (
                        <div className='flex justify-between items-center'>
                            <div className='cursor-pointer' onClick={() => setConfirmMassDelete(true)}>
                                {selectedFiles.length > 0 && <IconDelete />}
                            </div>
                            <div className='text-white font-semibold text-lg'>{selectedFiles.length} Selected</div>
                        </div>
                    )}
                </div>
            )}
        </AuthLayout>
    );
};

export default FilesDashboard;
